/* LoveSuze native drawer: server-rendered totals, line-key updates, no app dependency. */
(() => {
  const surfaces = [...document.querySelectorAll('[data-ls-cart], [data-ls-cart-page]')];
  if (!surfaces.length || window.LoveSuzeCart) return;
  let mutating = false;
  const controllers = [];
  const refreshAll = async () => {
    const results = await Promise.allSettled(controllers.map((controller) => controller.refresh()));
    const failure = results.find((result) => result.status === 'rejected');
    if (failure) throw failure.reason;
  };
  const busyAll = (value) => controllers.forEach((controller) => controller.busy(value));
  window.LoveSuzeCart = { refresh: () => Promise.all(controllers.map((controller) => controller.safeRefresh())) };
  surfaces.forEach((drawer) => {
  const isPage = drawer.hasAttribute('data-ls-cart-page');
  const content = drawer.querySelector('[data-ajax-cart-content]');
  const root = window.Shopify?.routes?.root || '/';
  let request = 0;
  let returnFocus;
  let isOpen = false;
  const status = (message) => {
    let region = drawer.querySelector('[data-ls-cart-status]');
    if (!region) {
      region = document.createElement('div');
      region.className = 'ls-cart-status';
      region.dataset.lsCartStatus = '';
      region.setAttribute('role', 'status');
      content.prepend(region);
    }
    region.textContent = message;
  };
  const busy = (value) => {
    drawer.setAttribute('aria-busy', String(value));
    drawer.querySelectorAll('[data-ls-quantity], .ls-cart-checkout').forEach((control) => {
      if (control.tagName === 'BUTTON') control.disabled = value;
    });
    drawer.querySelectorAll('[data-ls-rec]').forEach((card) => {
      const select = card.querySelector('[data-ls-rec-variant]');
      const button = card.querySelector('[data-ls-rec-add]');
      if (select) select.disabled = value || card.dataset.lsAdded === 'true';
      if (button) button.disabled = value || !select?.value || card.dataset.lsAdded === 'true';
    });
  };
  const loadBestSellers = async (revision) => {
    const section = drawer.querySelector('[data-ls-bestsellers]');
    if (!section) return;
    try {
      const response = await fetch(section.dataset.lsBestsellers, { cache: 'no-store', credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
      if (!response.ok) return;
      const view = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (revision !== request || !section.isConnected) return;
      const existing = new Set([...drawer.querySelectorAll('[data-ls-rec]')].map((card) => card.dataset.lsRec));
      const cards = [...view.querySelectorAll('[data-ls-rec]')].filter((card) => !existing.has(card.dataset.lsRec)).slice(0, 4);
      if (!cards.length) return;
      section.querySelector('[data-ls-carousel]').replaceChildren(...cards);
      section.hidden = false;
    } catch (_) { /* Optional suggestions must never prevent cart updates. */ }
  };
  const refresh = async () => {
    const revision = ++request;
    const activeControl = document.activeElement;
    const scrollTop = drawer.querySelector('.ls-cart-scroll')?.scrollTop || 0;
    const note = drawer.querySelector('[name="note"]')?.value;
    const choices = new Map([...drawer.querySelectorAll('[data-ls-rec]')].map((card) => [card.dataset.lsRec, card.querySelector('[data-ls-rec-variant]')?.value]));
    const response = await fetch(`${root}cart?view=${isPage ? 'lovesuze-page' : 'ajax'}&_ls=${Date.now()}`, { cache: 'no-store', credentials: 'same-origin', signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw new Error('Your bag could not be refreshed. Please reload the page to check it.');
    const documentView = new DOMParser().parseFromString(await response.text(), 'text/html');
    const template = documentView.querySelector('template[data-html="content"]');
    const options = documentView.querySelector('template[data-options]');
    if (!template?.content.querySelector('.ls-cart-heading') || !options) throw new Error('Your bag could not be refreshed. Please reload the page to check it.');
    const count = JSON.parse(options.innerHTML).item_count;
    if (revision !== request) return;
    content.replaceChildren(template.content.cloneNode(true));
    if (note !== undefined && drawer.querySelector('[name="note"]')) drawer.querySelector('[name="note"]').value = note;
    const restoreChoices = (scope = drawer) => {
      scope?.querySelectorAll('[data-ls-rec]').forEach((card) => {
      const select = card.querySelector('[data-ls-rec-variant]');
      const choice = choices.get(card.dataset.lsRec);
      if (select && choice && [...select.options].some((option) => option.value === choice && !option.disabled)) {
        select.value = choice;
        card.querySelector('[data-ls-rec-price]').textContent = select.selectedOptions[0].dataset.price;
      }
    });
    };
    restoreChoices();
    const scroller = drawer.querySelector('.ls-cart-scroll');
    if (scroller) scroller.scrollTop = scrollTop;
    document.querySelectorAll('[data-bind="itemCount"]').forEach((node) => {
      node.textContent = count;
      node.classList.toggle('has-cart-count', count > 0);
    });
    document.querySelectorAll('[data-ajax-cart-trigger]').forEach((node) => node.classList.toggle('has-cart-count', count > 0));
    busy(mutating);
    // Suggestions are optional: never hold cart updates or checkout for their response.
    loadBestSellers(revision).then(() => {
      if (revision !== request) return;
      restoreChoices(drawer.querySelector('[data-ls-bestsellers]'));
      busy(mutating);
    });
    if (isOpen && activeControl && !activeControl.isConnected) drawer.querySelector('[data-ajax-cart-close]')?.focus({ preventScroll: true });
  };
  const safeRefresh = () => refresh().catch((error) => status(error.message));
  controllers.push({ refresh, safeRefresh, busy });
  const close = () => {
    drawer.classList.remove('is-visible');
    document.querySelector('.ajax-cart__overlay')?.classList.remove('is-visible');
  };
  const focusables = () => [...drawer.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),textarea,summary,select')].filter((element) => element.getClientRects().length);
  const observeVisibility = () => {
    const open = drawer.classList.contains('is-visible');
    if (open === isOpen) return;
    isOpen = open;
    drawer.inert = !open;
    drawer.setAttribute('aria-hidden', String(!open));
    document.body.classList.toggle('ls-cart-open', open);
    if (open) {
      returnFocus = document.activeElement;
      (drawer.querySelector('[data-ajax-cart-close]') || drawer).focus();
      safeRefresh();
    } else if (returnFocus?.isConnected) returnFocus.focus({ preventScroll: true });
  };
  if (!isPage) {
    drawer.tabIndex = -1;
    new MutationObserver(observeVisibility).observe(drawer, { attributes: true, attributeFilter: ['class'] });
    observeVisibility();
  }
  document.addEventListener('keydown', (event) => {
    if (!isOpen) return;
    if (event.key === 'Escape') { event.preventDefault(); close(); }
    if (event.key === 'Tab') {
      const controls = focusables();
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (!first) { event.preventDefault(); drawer.focus(); return; }
      if (event.shiftKey && (document.activeElement === first || !drawer.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && (document.activeElement === last || !drawer.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
    }
  });
  drawer.addEventListener('change', (event) => {
    if (!event.target.matches('[data-ls-rec-variant]')) return;
    const select = event.target;
    const card = select.closest('[data-ls-rec]');
    const option = select.selectedOptions[0];
    if (option?.value && !option.disabled) card.querySelector('[data-ls-rec-price]').textContent = option.dataset.price;
    busy(mutating);
  });
  drawer.addEventListener('click', async (event) => {
    const arrow = event.target.closest('[data-ls-scroll]');
    if (arrow) {
      const track = arrow.closest('[data-ls-bestsellers]').querySelector('[data-ls-carousel]');
      track.scrollBy({ left: Number(arrow.dataset.lsScroll) * (track.clientWidth * .85), behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      return;
    }
    const button = event.target.closest('[data-ls-quantity], [data-ls-rec-add]');
    if (!button || mutating || button.disabled) return;
    event.preventDefault();
    const adding = button.hasAttribute('data-ls-rec-add');
    const card = button.closest('[data-ls-rec]');
    const line = button.closest('[data-ls-item]');
    const select = card?.querySelector('[data-ls-rec-variant]');
    const key = line?.dataset.lsItem;
    const quantity = Number(button.dataset.lsQuantity);
    const variantId = select?.value;
    if (adding) {
      if (!variantId || select.selectedOptions[0]?.disabled || card.dataset.lsAdded === 'true') return;
    } else if (!key || !Number.isInteger(quantity) || quantity < 0) return;
    let placementContext = null;
    try { if (adding) placementContext = window.LoveSuzeListTracking?.context(card); } catch (_) {}
    mutating = true;
    status('');
    busyAll(true);
    let added = false;
    try {
      const response = await fetch(`${root}cart/${adding ? 'add' : 'change'}.js`, {
        method: 'POST', credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(adding ? { items: [{ id: variantId, quantity: 1 }] } : { id: key, quantity })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Unable to update that item. Please try again.');
      if (adding) {
        added = true; card.dataset.lsAdded = 'true';
        try { window.LoveSuzeListTracking?.added(result, placementContext, 1); } catch (_) {}
      }
      await refreshAll();
      status(adding ? 'Item added to your bag.' : quantity === 0 ? 'Item removed from your bag.' : 'Bag updated.');
    } catch (error) {
      status(added ? 'Item added, but your bag could not refresh. Please reload the page to check it.' : error.message || 'Unable to update your bag. Please try again.');
    } finally {
      mutating = false;
      busyAll(false);
      if (isOpen || isPage) {
        const updatedLine = [...drawer.querySelectorAll('[data-ls-item]')].find((item) => item.dataset.lsItem === key);
        const focusTarget = adding && !added && card?.isConnected ? select : updatedLine?.querySelector('[data-ls-quantity]') || drawer.querySelector(isPage ? '.ls-cart-checkout' : '[data-ajax-cart-close]');
        focusTarget?.focus({ preventScroll: true });
      }
    }
  });
  drawer.addEventListener('submit', (event) => {
    if (mutating) event.preventDefault();
  });
  if (isPage) loadBestSellers(request).then(() => busy(mutating));
  });
})();
